import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import axios from "axios";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import Search from "./Search";
import Pagination from "./Pagination";
import DefaultEmployeeProfile from "../images/DefaultEmployeeProfile.svg";
import "../styles/AdminPanel.css";
import "../styles/UserList.css";
import { useAuth } from "../auth/auth";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import { PERMISSIONS } from "../utils/rbac";
import {
  fetchArchivedUserCount,
  fetchArchivedUsers,
  fetchAllUserCount,
  fetchAllUsers,
  fetchUserCount,
  fetchUsers,
} from "../services/UserService";

const apiUrl = process.env.REACT_APP_API_URL;

const getDefaultYear = () => {
  const now = new Date();
  const month = now.getMonth();
  const day = now.getDate();
  const year = now.getFullYear();

  if (month === 11 && day >= 19) return year + 1;
  return year;
};

function EmployeeYtd() {
  useAuth();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canManageYtd = isAllowed(PERMISSIONS.MANAGE_YTD);

  const [employeeFilter, setEmployeeFilter] = useState("all");

  const [users, setUsers] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(true);

  const [selectedYear, setSelectedYear] = useState(getDefaultYear());

  const [pageSize] = useState(15);
  const [count, setCount] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(pageParam);

  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [message, setMessage] = useState(null);
  const [isYtdModalOpen, setIsYtdModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [editedYtdAmount, setEditedYtdAmount] = useState("");
  const [currentRow, setCurrentRow] = useState(null);

  const yearsToShow = useMemo(() => {
    const ytdYear = getDefaultYear();
    return [ytdYear - 1, ytdYear];
  }, []);

  const showMessage = (text) => {
    setMessage(text);
    setTimeout(() => {
      setMessage(null);
    }, 2500);
  };

  const handleSearch = (resultUsers) => {
    setSearchResult(resultUsers);
    setShowAllUsers(false);
  };

  const handleShowAllUsers = () => {
    setShowAllUsers(true);
    setSearchResult([]);
  };

  const handleFilterChange = (value) => {
    setEmployeeFilter(value);
    setCurrentPage(1);
    setSearchResult([]);
    setShowAllUsers(true);
    setSearchParams({ page: 1 });
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setCurrentPage(1);
    setSearchResult([]);
    setShowAllUsers(true);
    setSearchParams({ page: 1 });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams({ page });
  };

  useEffect(() => {
    async function fetchCount() {
      try {
        const employeeCount =
          employeeFilter === "archived"
            ? await fetchArchivedUserCount()
            : employeeFilter === "active"
            ? await fetchUserCount()
            : await fetchAllUserCount();
        setCount(employeeCount);
      } catch (e) {}
    }
    fetchCount();
  }, [employeeFilter]);

  useEffect(() => {
    const fetchAllEmployees = async () => {
      try {
        setLoading(true);
        if (searchResult.length > 0 && !showAllUsers) {
          setUsers(searchResult);
          return;
        }

        const res =
          employeeFilter === "archived"
            ? await fetchArchivedUsers(currentPage, pageSize, selectedYear)
            : employeeFilter === "active"
            ? await fetchUsers(currentPage, pageSize, selectedYear)
            : await fetchAllUsers(currentPage, pageSize, selectedYear);
        setUsers(res.map((x) => x));
      } catch (e) {
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    fetchAllEmployees();
  }, [
    currentPage,
    pageSize,
    searchResult,
    showAllUsers,
    employeeFilter,
    selectedYear,
  ]);

  const handleEditYtdSubmit = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${apiUrl}/updateYTDFinal`, {
        invoiceId: selectedInvoiceId,
        newYTDFinal: parseFloat(editedYtdAmount),
      });

      const data = response.data;
      if (response.status === 200 && data.status === 1) {
        setIsYtdModalOpen(false);
        showMessage("YTD Amount updated successfully");

        const updatedFinal = Number(editedYtdAmount || 0);
        if (currentRow?.id) {
          setUsers((prev) =>
            prev.map((u) =>
              String(u.id) === String(currentRow.id)
                ? { ...u, finalYTD: updatedFinal }
                : u
            )
          );
          setSearchResult((prev) =>
            prev.map((u) =>
              String(u.id) === String(currentRow.id)
                ? { ...u, finalYTD: updatedFinal }
                : u
            )
          );
          setCurrentRow((prev) =>
            prev ? { ...prev, finalYTD: updatedFinal } : prev
          );
        }
      } else {
        showMessage("Error: " + (data?.message || "Failed"));
      }
    } catch (e) {
      showMessage("An error occured");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-panel-parent-container">
      <PermissionDialog />
      <NavBar />
      <TopNavBar />

      {message && (
        <div className="custom-modal">
          <div className="modal-content">{message}</div>
        </div>
      )}

      {isYtdModalOpen && currentRow && (
        <div className="custom-modal">
          <div
            className="modal-content-ytd"
            style={{ minWidth: "400px", maxWidth: "400px" }}
          >
            <h3 style={{ marginBottom: "16px" }}>Edit YTD Amount</h3>

            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              YTD Amount:
            </label>
            <input
              type="number"
              step="0.01"
              value={editedYtdAmount}
              onChange={(e) => setEditedYtdAmount(e.target.value)}
              placeholder="Enter YTD amount"
              style={{
                width: "94%",
                padding: "10px",
                marginBottom: "16px",
                fontSize: "15px",
                border: "2px solid #2E8056",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                className="edit-ytd-submit"
                onClick={() => setIsYtdModalOpen(false)}
              >
                Cancel
              </button>
              <button className="edit-ytd-submit" onClick={handleEditYtdSubmit}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-card admin-panel">
        <div className="admin-card-body">
          <div className="admin-header">
            <div
              className="left-section"
              style={{ display: "flex", gap: "50px", alignItems: "center" }}
            >
              <h1 className="admin-header-title">
                <Link
                  to="/manageEmployee"
                  className="admin-card-title-link custom-link"
                >
                  <span className="fade-text">Manage Employee</span>
                </Link>
                {" > "}
                <span className="admin-card-title-span">Employee YTD</span>
              </h1>
              <div>
                {yearsToShow.map((y) => (
                  <button
                    key={y}
                    style={{
                      padding: "4px 10px",
                      marginRight: "8px",
                      borderRadius: "8px",
                      border: "none",
                      cursor: "pointer",
                      backgroundColor:
                        selectedYear === y ? "#2E8056" : "#E0E0E0",
                      color: selectedYear === y ? "#fff" : "#333",
                      fontWeight: 500,
                    }}
                    onClick={() => handleYearChange(y)}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>

            <div
              className="right-section"
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
              }}
            >
              {/* <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginBottom: "2px",
                }}
              > */}
              <select
                value={employeeFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                style={{
                  padding: "4px 10px",
                  borderRadius: "8px",
                  border: "1px solid #d0d0d0",
                  minWidth: "240px",
                }}
              >
                <option value="all">All Employees</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
              {/* </div> */}

              <Search
                onSearch={handleSearch}
                onClear={handleShowAllUsers}
                searchPath={
                  employeeFilter === "archived"
                    ? "/searchArchived"
                    : employeeFilter === "active"
                    ? "/search"
                    : "/searchAll"
                }
                year={selectedYear}
              />
            </div>
          </div>

          {loading && (
            <div className="spinner-container">
              <TailSpin
                type="TailSpin"
                color="rgb(155, 236, 34)"
                height={70}
                width={70}
                timeout={5000}
              />
            </div>
          )}

          <div className="user-list user-list-margin">
            <table className="user-table">
              <thead>
                <tr>
                  <th>Employee Id</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>YTD Amount($)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="">
                      <Link
                        to={"/userProfile"}
                        state={user.id}
                        className="user-link"
                      >
                        #{user.id}
                      </Link>
                    </td>
                    <td className="user-info">
                      <div className="user-container">
                        {user.image ? (
                          <div className="user-list-image">
                            <img src={user.image} alt="Employee Image" />
                          </div>
                        ) : (
                          <div className="user-list-image">
                            <img
                              src={DefaultEmployeeProfile}
                              alt="Default Employee Image"
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <Link
                        to={"/userProfile"}
                        state={user.id}
                        className="user-link"
                      >
                        {user.name
                          ? user.name.charAt(0).toUpperCase() +
                            user.name.slice(1)
                          : ""}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={"/userProfile"}
                        state={user.id}
                        className="user-link"
                      >
                        {user.email}
                      </Link>
                    </td>
                    <td>
                      {user.is_archived === "yes" ? "Archived" : "Active"}
                    </td>
                    <td>{Number(user.finalYTD || 0).toFixed(2)}</td>
                    <td>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          guardAction(PERMISSIONS.MANAGE_YTD, "edit YTD amount", () => {
                            if (!user.latestInvoiceId) {
                              showMessage(
                                "No invoice found for this employee in the selected year"
                              );
                              return;
                            }

                            setIsYtdModalOpen(true);
                            setSelectedInvoiceId(user.latestInvoiceId);
                            setCurrentRow(user);
                            setEditedYtdAmount(
                              Number(user.finalYTD || 0).toFixed(2)
                            );
                          });
                        }}
                        style={{
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                        }}
                        className={deniedActionClass(canManageYtd)}
                        title="Edit YTD Amount"
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {searchResult.length === 0 && (
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalUsers={count}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default EmployeeYtd;
